import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Zap, Trophy, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const getWsUrl = () => {
  if (BACKEND_URL && BACKEND_URL.startsWith('http')) {
    return BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
  }
  const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  return `${protocol}${window.location.host}`;
};

const SpeedRunParticipant = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [checkingExistingParticipant, setCheckingExistingParticipant] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [quizState, setQuizState] = useState('waiting'); // waiting, countdown, active, answered, ended
  const [countdown, setCountdown] = useState(3);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerResult, setAnswerResult] = useState(null);
  const [myScore, setMyScore] = useState(0);
  const [ws, setWs] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [finalResults, setFinalResults] = useState(null);
  const [myRank, setMyRank] = useState(null);
  const timerRef = useRef(null);
  const [rankedOptions, setRankedOptions] = useState([]);

  useEffect(() => {
    const initializeParticipant = async () => {
      console.log('[INIT] Starting participant initialization for session:', sessionId);
      
      // Check if participant has already joined (restore from localStorage)
      const storedParticipantId = localStorage.getItem(`participant_${sessionId}`);
      const storedParticipantName = localStorage.getItem(`participant_name_${sessionId}`);
      
      console.log('[INIT] Found in localStorage - ID:', storedParticipantId, 'Name:', storedParticipantName);
      
      if (storedParticipantId && storedParticipantName) {
        console.log('[INIT] ✓ Restoring participant from localStorage:', storedParticipantId);
        setParticipantId(storedParticipantId);
        setParticipantName(storedParticipantName);
        setJoined(true);
        
        console.log('[INIT] State updated: joined=true, fetching score...');
        
        // Fetch current score from server
        await fetchScoreForParticipant(storedParticipantId);
      } else {
        console.log('[INIT] No existing participant found in localStorage');
      }
      
      // Done checking
      console.log('[INIT] Initialization complete, setting checkingExistingParticipant=false');
      setCheckingExistingParticipant(false);
    };
    
    fetchSession();
    initializeParticipant();
  }, [sessionId]);
  
  const fetchScoreForParticipant = async (pid) => {
    try {
      console.log('[SCORE] Fetching score for participant:', pid);
      const response = await axios.get(`${API}/speed-run/${sessionId}/leaderboard`);
      const myData = response.data.participants?.find(p => p.participant_id === pid);
      if (myData) {
        console.log('[SCORE] ✓ Restored score from server:', myData.total_score);
        setMyScore(myData.total_score);
      } else {
        console.log('[SCORE] Participant not found in leaderboard');
      }
    } catch (error) {
      console.error('[SCORE] Failed to restore score:', error);
    }
  };

  useEffect(() => {
    if (joined && session) {
      connectWebSocket();
    }
    
    return () => {
      if (ws) ws.close();
    };
  }, [joined, session]);

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`);
      setSession(response.data);
    } catch (error) {
      toast.error('Failed to load quiz');
    }
  };

  const fetchMyScore = async () => {
    try {
      const response = await axios.get(`${API}/speed-run/${sessionId}/leaderboard`);
      const myData = response.data.participants?.find(p => p.participant_id === participantId);
      if (myData) {
        console.log('✓ Fetched score from server:', myData.total_score);
        setMyScore(myData.total_score);
      }
    } catch (error) {
      console.error('Failed to fetch score:', error);
    }
  };

  const connectWebSocket = (isReconnect = false) => {
    const wsUrl = getWsUrl();
    const websocket = new WebSocket(`${wsUrl}/api/ws/${sessionId}?participant_id=${participantId}&role=participant`);
    let pingInterval;
    let reconnectTimeout;
    
    websocket.onopen = () => {
      console.log('✅ Participant WebSocket connected');
      
      // If reconnecting, fetch current score
      if (isReconnect) {
        fetchMyScore();
      }
      
      // Start heartbeat ping every 30 seconds
      pingInterval = setInterval(() => {
        if (websocket.readyState === WebSocket.OPEN) {
          websocket.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };
    
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      console.log('WebSocket message:', data);
      
      // Handle different game events
      if (data.type === 'quiz_started') {
        setQuizState('waiting');
        toast.info('Quiz is starting!');
      } else if (data.type === 'countdown_start') {
        // Keep participants in waiting state during countdown (no countdown display)
        setQuizState('waiting');
        toast.info('Next question coming up...');
      } else if (data.type === 'question_start') {
        setQuizState('active');
        setCurrentQuestion(data.question);
        setSelectedAnswer(null);
        setAnswerResult(null);
        setRankedOptions([]);
        
        const timeLimit = data.time_limit || 30;
        
        // Use server start time if provided (for late joiners or refreshes)
        let startTime;
        if (data.server_start_time) {
          // Server sent the actual start time, use it
          startTime = new Date(data.server_start_time).getTime() + 3000; // Add 3s for countdown
        } else if (data.remaining_time != null) {
          // Late joiner - calculate start time from remaining time
          startTime = Date.now() - ((timeLimit - data.remaining_time) * 1000);
        } else {
          // New question starting now
          startTime = Date.now();
        }
        
        setStartTime(startTime);
        
        // Calculate time remaining based on elapsed time
        const calculateTimeRemaining = () => {
          const elapsed = (Date.now() - startTime) / 1000;
          const remaining = Math.max(0, timeLimit - elapsed);
          return Math.ceil(remaining);
        };
        
        setTimeRemaining(calculateTimeRemaining());
        
        // Clear any existing timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        
        // Update timer every 100ms for smooth countdown
        timerRef.current = setInterval(() => {
          const remaining = calculateTimeRemaining();
          setTimeRemaining(remaining);
          
          if (remaining <= 0) {
            clearInterval(timerRef.current);
          }
        }, 100);
      } else if (data.type === 'question_end') {
        if (quizState !== 'answered') {
          setQuizState('ended');
        }
      } else if (data.type === 'quiz_end') {
        setQuizState('ended');
        setFinalResults(data.leaderboard);
        
        // Find my rank in the leaderboard
        const myResult = data.leaderboard.find(p => p.participant_id === participantId);
        if (myResult) {
          setMyRank(myResult.rank);
          setMyScore(myResult.total_score);
        }
        
        toast.info('Quiz completed!');
      }
    };
    
    websocket.onerror = (error) => {
      console.error('❌ Participant WebSocket error:', error);
    };
    
    websocket.onclose = () => {
      console.log('🔌 Participant WebSocket disconnected');
      clearInterval(pingInterval);
      
      // Attempt reconnection after 2 seconds
      reconnectTimeout = setTimeout(() => {
        console.log('🔄 Participant attempting to reconnect...');
        connectWebSocket(true);
      }, 2000);
    };
    
    setWs(websocket);
    
    // Cleanup function
    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimeout);
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  };

  const handleJoinQuiz = async () => {
    if (joining || joined) {
      console.log('[JOIN] Already joining or joined, skipping');
      return; // Prevent duplicate calls or joining when already joined
    }
    
    console.log('[JOIN] Starting join process...');
    setJoining(true);
    try {
      // Check if we have an existing participant_id in localStorage
      const storedParticipantId = localStorage.getItem(`participant_${sessionId}`);
      console.log('DEBUG - Stored participant_id from localStorage:', storedParticipantId);
      console.log('DEBUG - Session is_anonymous:', session?.is_anonymous);
      console.log('DEBUG - Current participantName:', participantName);
      
      const payload = {
        ...(session.is_anonymous && !participantName ? {} : { participant_name: participantName }),
        ...(storedParticipantId ? { participant_id: storedParticipantId } : {})
      };
      
      console.log('DEBUG - Final payload being sent:', payload);
      const response = await axios.post(`${API}/speed-run/${sessionId}/join`, payload);
      console.log('DEBUG - Join response:', response.data);
      
      setParticipantId(response.data.participant_id);
      setParticipantName(response.data.participant_name);
      setJoined(true);
      
      // Store participant_id AND participant_name in localStorage for this session
      localStorage.setItem(`participant_${sessionId}`, response.data.participant_id);
      localStorage.setItem(`participant_name_${sessionId}`, response.data.participant_name);
      console.log('[JOIN] ✓ Saved to localStorage - ID and Name');
      
      toast.success(`Welcome ${response.data.participant_name}!`);
    } catch (error) {
      console.error('Join error:', error);
      toast.error('Failed to join quiz');
      setJoining(false);
    }
  };

  const handleSubmitAnswer = async (answer) => {
    if (selectedAnswer) return;
    
    setSelectedAnswer(answer);
    
    // Calculate time taken
    const endTime = Date.now();
    const timeTaken = (endTime - startTime) / 1000; // Convert to seconds
    
    const payload = {
      question_id: currentQuestion.id,
      answer: answer,
      participant_id: participantId,
      participant_name: participantName,
      time_taken: timeTaken
    };
    
    console.log('DEBUG - Submitting answer payload:', payload);
    
    try {
      const response = await axios.post(`${API}/speed-run/${sessionId}/submit-answer`, payload);
      
      console.log('DEBUG - Answer response:', response.data);
      setAnswerResult(response.data);
      setMyScore(prev => prev + response.data.points_awarded);
      setQuizState('answered');
      
      if (response.data.is_correct) {
        toast.success(`✓ Correct! +${response.data.points_awarded} points`);
      } else {
        toast.error(`✗ Wrong! ${response.data.points_awarded} points`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
      setSelectedAnswer(null);
    }
  };

  const handleRankingSelect = (optionText) => {
    setRankedOptions(prev => {
      // If already ranked, remove it (reset)
      if (prev.includes(optionText)) {
        return prev.filter(o => o !== optionText);
      }
      // Otherwise add to ranking
      return [...prev, optionText];
    });
  };

  const handleSubmitRanking = async () => {
    if (rankedOptions.length !== currentQuestion.options?.length) {
      toast.error('Please rank all options');
      return;
    }
    
    // Submit ranking as comma-separated string
    const rankingAnswer = rankedOptions.join(',');
    console.log('DEBUG - Submitting ranking:', rankingAnswer);
    console.log('DEBUG - Ranked options array:', rankedOptions);
    await handleSubmitAnswer(rankingAnswer);
  };

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Zap className="h-12 w-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-lg text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  // Join Screen
  // Show loading while checking for existing participant
  if (checkingExistingParticipant || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!joined) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mx-auto mb-6">
              <Zap className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-3xl font-bold mb-2">{session.topic}</h1>
            <div className="inline-block px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-6">
              Speed Run Quiz
            </div>
            
            {!session.is_anonymous && (
              <div className="mb-6">
                <Input
                  type="text"
                  value={participantName}
                  onChange={(e) => setParticipantName(e.target.value)}
                  placeholder="Enter your name"
                  className="text-center text-lg"
                />
              </div>
            )}
            
            <Button
              onClick={handleJoinQuiz}
              disabled={joining || (!session.is_anonymous && !participantName.trim())}
              className="w-full py-6 text-lg bg-gradient-to-r from-orange-500 to-red-600"
            >
              {joining ? 'Joining...' : 'Join Quiz'}
            </Button>
            
            {session.is_anonymous && (
              <p className="text-sm text-gray-500 mt-4">
                Anonymous mode - You'll be assigned a random name
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active Question State - Show only options, NOT the question text
  if (quizState === 'active' && currentQuestion) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%)' }}>
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          {/* Timer */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-full font-bold text-2xl shadow-lg">
              <span>{timeRemaining}s</span>
            </div>
          </div>

          {/* Options Display */}
          <Card className="mb-6 border-4 border-orange-500">
            <CardContent className="p-8">
              {console.log('DEBUG - Question response_type:', currentQuestion.response_type)}
              <h2 className="text-2xl font-bold text-center mb-8 text-gray-600">
                {currentQuestion.response_type === 'drag_reorder_ranking' ? 'Rank the options (click to select order):' : 'Select your answer:'}
              </h2>

              {/* MCQ or MCQ with Images */}
              {(currentQuestion.response_type === 'mcq_response' || currentQuestion.response_type === 'image_mcq_response') && (
                <div className="grid grid-cols-1 gap-4">
                  {currentQuestion.options?.map((option, idx) => (
                    <div
                      key={option.id}
                      onClick={() => !selectedAnswer && timeRemaining > 0 && handleSubmitAnswer(option.text)}
                      className={`cursor-pointer rounded-lg border-2 transition-all ${
                        selectedAnswer === option.text
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-orange-500 hover:border-orange-600 hover:shadow-lg'
                      } ${selectedAnswer !== null || timeRemaining === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {option.image_url ? (
                        // Layout with image
                        <div className="p-4 flex flex-col items-center">
                          <img 
                            src={option.image_url} 
                            alt={`Option ${String.fromCharCode(65 + idx)}`}
                            className="w-48 h-48 object-cover rounded-lg mb-3"
                          />
                          <div className="flex items-center justify-center mt-3">
                            <span className="font-bold text-2xl mr-3 text-orange-600">{String.fromCharCode(65 + idx)}</span>
                            <span className="text-lg font-semibold">{option.text}</span>
                          </div>
                        </div>
                      ) : (
                        // Layout without image
                        <div className="p-4 flex items-center">
                          <span className="font-bold text-2xl mr-4 text-orange-600">{String.fromCharCode(65 + idx)}</span>
                          <span className="text-lg font-semibold">{option.text}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Ranking Question Type */}
              {currentQuestion.response_type === 'drag_reorder_ranking' && (
                <>
                  <div className="grid grid-cols-1 gap-4 mb-6">
                    {currentQuestion.options?.map((option, idx) => {
                      const rankPosition = rankedOptions.indexOf(option.text);
                      const isRanked = rankPosition !== -1;
                      
                      return (
                        <Button
                          key={option.id}
                          onClick={() => handleRankingSelect(option.text)}
                          disabled={selectedAnswer !== null || timeRemaining === 0}
                          className={`w-full py-6 text-xl justify-start relative ${
                            isRanked
                              ? 'bg-blue-500 hover:bg-blue-600'
                              : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
                          }`}
                        >
                          <span className="font-bold mr-4">{String.fromCharCode(65 + idx)}</span>
                          <span className="flex-1">{option.text}</span>
                          {isRanked && (
                            <span className="absolute right-4 w-8 h-8 rounded-full bg-white text-blue-600 font-bold flex items-center justify-center">
                              {rankPosition + 1}
                            </span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    onClick={handleSubmitRanking}
                    disabled={selectedAnswer !== null || rankedOptions.length !== currentQuestion.options?.length || timeRemaining === 0}
                    className="w-full py-4 text-xl bg-green-600 hover:bg-green-700"
                  >
                    Submit Ranking ({rankedOptions.length}/{currentQuestion.options?.length})
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Score Display */}
          <Card className="border-2 border-blue-500">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Your current score</p>
              <p className="text-3xl font-bold text-blue-600">{myScore}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Answered State
  if (quizState === 'answered' && answerResult) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%)' }}>
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            {answerResult.is_correct ? (
              <>
                <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-green-600 mb-2">Correct!</h2>
                <p className="text-xl text-gray-700 mb-4">+{answerResult.points_awarded} points</p>
              </>
            ) : (
              <>
                <XCircle className="h-20 w-20 text-red-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-red-600 mb-2">Incorrect</h2>
                <p className="text-xl text-gray-700 mb-4">{answerResult.points_awarded} points</p>
              </>
            )}
            
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Your time</p>
              <p className="text-2xl font-bold text-blue-600">{answerResult.time_taken?.toFixed(2)}s</p>
            </div>

            <div className="mt-4 p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-600">Total score</p>
              <p className="text-3xl font-bold text-orange-600">{Number(myScore).toFixed(2)}</p>
            </div>

            <p className="text-gray-600 mt-6">Waiting for next question...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Final Results State
  if (quizState === 'ended' && finalResults) {
    const podiumColors = ['#FFD700', '#C0C0C0', '#CD7F32']; // Gold, Silver, Bronze
    
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
        <Card className="max-w-2xl w-full">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <Trophy 
                className="h-20 w-20 mx-auto mb-4" 
                style={{ color: myRank <= 3 ? podiumColors[myRank - 1] : '#6b7280' }}
              />
              <h2 className="text-4xl font-bold mb-2">Quiz Complete!</h2>
              <div className="inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full font-bold text-2xl">
                Rank #{myRank}
              </div>
            </div>

            {/* My Score */}
            <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-orange-50 rounded-lg border-2 border-orange-500">
              <p className="text-center text-sm text-gray-600 mb-2">Your Final Score</p>
              <p className="text-center text-5xl font-bold text-orange-600">{Number(myScore).toFixed(2)}</p>
            </div>

            {/* Top 3 Leaderboard */}
            <div className="space-y-3">
              <h3 className="text-xl font-bold text-center mb-4">Top Performers</h3>
              {finalResults.slice(0, 3).map((participant, idx) => (
                <div 
                  key={participant.participant_id}
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    participant.participant_id === participantId 
                      ? 'bg-orange-100 border-2 border-orange-500' 
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="text-3xl font-bold"
                      style={{ color: podiumColors[idx] }}
                    >
                      #{idx + 1}
                    </div>
                    <div>
                      <p className="font-bold text-lg">{participant.participant_name.replace(/^Player\s+/i, '')}</p>
                      <p className="text-sm text-gray-600">
                        {participant.correct_answers || 0} correct
                      </p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-orange-600">
                    {Number(participant.total_score).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            {/* My Position if not in top 3 */}
            {myRank > 3 && (
              <div className="mt-6 p-4 bg-orange-100 border-2 border-orange-500 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-2xl font-bold text-gray-700">#{myRank}</div>
                    <p className="font-bold text-lg">{participantName}</p>
                  </div>
                  <p className="text-2xl font-bold text-orange-600">{Number(myScore).toFixed(2)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Waiting for Quiz to Start
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%)' }}>
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <Zap className="h-16 w-16 text-orange-500 mx-auto mb-4 animate-pulse" />
          <h2 className="text-2xl font-bold mb-2">Ready to Race!</h2>
          <p className="text-gray-600 mb-4">
            Waiting for the quiz to start...
          </p>
          <div className="inline-block px-4 py-2 bg-orange-100 text-orange-600 rounded-full font-bold">
            {participantName}
          </div>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">Your current score</p>
            <p className="text-4xl font-bold text-blue-600">{myScore}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SpeedRunParticipant;
